import { DispatchFunction, ActionMessage } from 'satcheljs';
import { LegacyMiddleware } from 'satcheljs/lib/legacy';
import { satchelActionDispatched } from './satchelActionDispatched';
import { isFeatureEnabled } from 'owa-feature-flags';
import { warnWhenDispatchedInMutator } from '../utils/warnWhenDispatchedInMutator';
import { areFeatureFlagsInitialized } from 'owa-feature-flags/lib/actions/initializeFeatureFlags';

const DIAGNOSTICS_ACTION_PREFIX = '__DIAGNOSTICS';

export const satchelDiagnosticsMiddleware = (next: DispatchFunction, action: ActionMessage) => {
    if (
        // We don't want to call `isFeatureEnabled` before the feature flags are loaded.
        // However, loading the feature flags itself involves calling an action.
        // So, we skip logging diagnostics for this action.
        areFeatureFlagsInitialized() &&
        isFeatureEnabled('fwk-devTools') &&
        isFeatureEnabled('fwk-betterDiagnostics-satchel') &&
        // Dispatching an action in the action processing middleware will cause an infinite loop
        // so we filter out the action type that we are about to dispatch.
        // Matching on the prefix __DIAGNOSTICS means that other diagnostics actions can also be
        // filtered out (if they follow the same naming convention) to reduce noise in the satchel
        // action diagnostics logs.
        action.type.indexOf(DIAGNOSTICS_ACTION_PREFIX) !== 0
    ) {
        satchelActionDispatched(action);
    }

    next(action);
};

export const legacySatchelDiagnosticsMiddleware: LegacyMiddleware = (
    next,
    action,
    actionType,
    args,
    actionContext
) => {
    // Same applies here as above. Legacy satchel actions have a slightly different shape.
    if (
        isFeatureEnabled('fwk-devTools') &&
        isFeatureEnabled('fwk-betterDiagnostics-satchel') &&
        actionType.indexOf(DIAGNOSTICS_ACTION_PREFIX) !== 0
    ) {
        const loggedAction = {
            type: actionType,
            args: args,
        };

        // We've seen some cases of satchel 3 mutators dispatching legacy satchel actions.
        // Since satchel throws when dispatching a satchel 3 action in a mutator but not on
        // legacy satchel actions, we'll log asynchronously via requestAnimationFrame to escape
        // the mutator's call stack
        //
        // So that we don't swallow all exceptions, if we aren't in a mutator, we'll re-throw
        // to continue surfacing real action exceptions for development
        //
        // This should be removed if Satchel issue 123 is fixed.
        // VSO: 50758
        warnWhenDispatchedInMutator(
            () => satchelActionDispatched(loggedAction),
            `[Anti-Pattern] Legacy action ${actionType} was dispatched inside of a mutator`
        );
    }

    next(action, actionType, args, actionContext);
};
